import { BRAND, faceOf, type Coord, type Letter, type Tile } from '@nt2/engine';
import { audio } from '../audio/engine.js';
import { prefs } from '../state/prefs.js';
import type { Spot } from './arrange.js';
import { clamp, el, on, setAttr, setClass, setDisabled, setStyle, setText } from './dom.js';
import { icon } from './icons.js';

/** Inner padding of the rack strip, and the smallest a letter may shrink to. */
const PAD = 4;
const MIN_TILE = 22;
import type { Board, DropTarget } from './board.js';

/** Where in the rack a dragged letter would go: onto a letter, or between two. */
export type RackSlot = { kind: 'swap'; tileId: string } | { kind: 'insert'; index: number };

export interface RackCallbacks {
  onPlace(tileId: string, to: Coord): void;
  /** Exchange two of the player's letters, wherever they are. */
  onSwap(a: string, b: string): void;
  /** Move a letter to a free square or a place in the rack. */
  onMove(tileId: string, to: Spot): void;
  onDumpRequest(tileId: string): void;
  onReorder(order: string[]): void;
  onCall(): void;
  onStuck(stuck: boolean): void;
}

/**
 * The player's hand.
 *
 * Tiles are absolutely positioned inside a scroller so they can be dragged out
 * without the layout reflowing under the finger — which is what makes a
 * 21-tile rack on a phone feel like objects rather than a list.
 */
export class Rack {
  readonly root: HTMLElement;
  #strip: HTMLElement;
  #inner: HTMLElement;
  #callBtn: HTMLButtonElement;
  #cool: HTMLElement;
  #emptyMsg: HTMLElement;
  #tileSize = 36;
  #stuckBtn: HTMLButtonElement;
  #stuckTally: HTMLElement;
  #stuck = false;
  #tiles: Tile[] = [];
  #nodes = new Map<string, HTMLElement>();
  #cell = 46;
  #lockedUntil = 0;
  #coolTimer = 0;
  /** Whether holding a letter offers to trade it in (a match rule, off by default). */
  #tradeEnabled = false;
  setTradeEnabled(on: boolean): void { this.#tradeEnabled = on; }
  /** The line showing where in the rack a dragged letter would slot in. */
  #caret: HTMLElement;

  constructor(private cb: RackCallbacks, private board: Board) {
    // Two elements, not one: the outer box is the scroll viewport and must be
    // free to shrink, while the inner box carries the width of the tiles. With
    // the width on the viewport instead, a long rack pushed the call button
    // off the side of a phone.
    this.#inner = el('div', { class: 'rack__inner' });
    this.#caret = el('div', { class: 'rack__caret', 'aria-hidden': 'true' });
    this.#caret.hidden = true;
    this.#inner.append(this.#caret);
    // The empty-rack prompt used to be CSS `content`, which cannot carry a
    // number that changes with the match settings.
    this.#emptyMsg = el('div', { class: 'rack__empty' });
    this.#strip = el('div', {
      class: 'rack__tiles', role: 'list',
      'aria-label': 'Your letters. Drag onto the grid, or type them.',
    }, this.#inner, this.#emptyMsg);
    this.#cool = el('div', { class: 'callbtn__cool' });
    this.#callBtn = el('button', {
      class: 'callbtn', type: 'button', disabled: true,
      'aria-label': 'Declare — everyone draws',
      title: 'Press this when you have used every letter and all your words are ' +
             'real. Everyone at the table then draws more letters.',
    }, el('span', { text: BRAND.callPhrase(2) }), this.#cool);
    on(this.#callBtn, 'click', () => this.cb.onCall());

    // The escape hatch. Always present, never configurable away: a table can
    // reach a position nobody can play out of, and the alternative to this is
    // abandoning the match.
    this.#stuckTally = el('span', { class: 'stuckbtn__tally' });
    this.#stuckBtn = el('button', {
      class: 'btn btn--icon stuckbtn', type: 'button',
      'aria-pressed': 'false',
    }, icon('stuck', 23), this.#stuckTally) as HTMLButtonElement;
    on(this.#stuckBtn, 'click', () => this.cb.onStuck(!this.#stuck));

    this.root = el('div', { class: 'rack' },
      this.#strip,
      el('div', { class: 'rack__actions' },
        el('button', {
          class: 'btn btn--icon', type: 'button',
          title: 'Shuffle your letters (\\) — reorders your rack only. It changes ' +
                 'nothing about the game; it is for shaking a word loose when you ' +
                 'have stared at the same order for too long.',
          'aria-label': 'Shuffle your letters',
        }, icon('shuffle', 23)),
        this.#stuckBtn,
        this.#callBtn));

    const shuffleBtn = this.root.querySelector('button') as HTMLButtonElement;
    on(shuffleBtn, 'click', () => this.shuffle());

    this.#bindDrag();
    /*
     * Re-lay the rack out when it changes size, not merely re-read the cell
     * size.
     *
     * The tiles are positioned once, from the strip's width at the moment they
     * arrive. On a phone the first measurement happens before the flex column
     * has settled, so the rack was laid out for a width it never had — the
     * letters were sized for a wider strip and the last one ran off the end.
     * Rotating the device had the same effect, permanently.
     */
    new ResizeObserver(() => {
      this.#measure();
      if (this.#tiles.length) this.setTiles(this.#tiles);
    }).observe(this.root);
  }

  /**
   * How big a rack letter is allowed to get.
   *
   * Deliberately larger than a board cell — `--rack-cell` in the stylesheet.
   * The rack is the thing you read constantly and tap with a thumb, and on a
   * phone it was coming out at the same size as a square on a board you can
   * zoom. It is a cap, not a size: {@link #layout} still shrinks to fit.
   */
  #measure(): void {
    const cs = getComputedStyle(this.root);
    const n = parseFloat(cs.getPropertyValue('--rack-cell'))
      || parseFloat(cs.getPropertyValue('--cell'));
    if (Number.isFinite(n) && n > 0) this.#cell = n;
  }

  /**
   * Fit every letter on screen at once.
   *
   * A physical rack sits in front of you entire; you look along it and see the
   * shapes. Scrolling a rack turns that into a memory exercise, which is a
   * different and much harder game. So the tiles shrink to fit rather than
   * running off the edge, down to a floor where they are still readable and
   * still a big enough target for a thumb, and only wrap to a second row if
   * even that will not do it.
   */
  /**
   * Fit every letter on screen, as large as they will go.
   *
   * One row is preferred, because a rack you read along in one sweep is the
   * whole point. But a second row is better than letters too small to read: on
   * a phone holding ten or twelve letters, one row means 22px tiles, while two
   * rows of six means 45px ones. So both are costed and the one that gives the
   * bigger letter wins, up to two rows — past that the rack starts eating the
   * board.
   */
  #layout(count: number): { size: number; perRow: number; rows: number; gap: number } {
    const gap = 4;
    const avail = Math.max(120, this.#strip.clientWidth - 2 * PAD);
    if (count === 0) return { size: this.#cell, perRow: 1, rows: 1, gap };

    const fit = (rows: number): { size: number; perRow: number; rows: number; gap: number } => {
      const perRow = Math.ceil(count / rows);
      const ideal = Math.floor((avail - gap * (perRow - 1)) / perRow);
      return { size: Math.min(this.#cell, Math.max(MIN_TILE, ideal)), perRow, rows, gap };
    };

    const one = fit(1);
    if (one.size >= this.#cell) return one;          // already as big as allowed
    const two = fit(2);
    // A second row has to earn itself: a marginal gain is not worth the extra
    // height, and it must genuinely fit.
    return two.size > one.size * 1.25 ? two : one;
  }

  setTiles(tiles: Tile[]): void {
    // The letter being dragged has left the rack some other way (typed onto
    // the board, traded in): that drag has nothing left to drop.
    const dragging = this.#dragging();
    if (dragging && !tiles.some((t) => t.id === dragging)) this.#cancelDrag();
    this.#tiles = tiles;
    this.#measure();
    const { size, perRow, rows, gap } = this.#layout(tiles.length);
    this.#tileSize = size;
    // Scoped to the rack, so shrinking these never touches the board's cells.
    this.#inner.style.setProperty('--cell', `${size}px`);
    this.#strip.style.height = `${rows * size + (rows - 1) * gap + 2 * PAD}px`;
    const seen = new Set<string>();
    tiles.forEach((tile, i) => {
      seen.add(tile.id);
      let node = this.#nodes.get(tile.id);
      if (!node) {
        node = el('div', { class: 'tile', 'data-tile': tile.id, role: 'listitem' },
          el('span'), el('span', { class: 'tile__value' }));
        node.classList.add('tile--dealt');
        setTimeout(() => node?.classList.remove('tile--dealt'), 460);
        this.#nodes.set(tile.id, node);
        this.#inner.append(node);
      }
      const face = faceOf(tile);
      setText(node.firstElementChild as HTMLElement, face ?? '★');
      setText(node.lastElementChild as HTMLElement,
        tile.value > 0 ? String(tile.value) : '');
      setClass(node, 'tile--blank', tile.isBlank);
      setClass(node, 'tile--unassigned', tile.isBlank && !tile.assigned);
      setAttr(node, 'aria-label', tile.isBlank ? 'blank tile' : `letter ${face}`);
      const col = i % perRow;
      const row = Math.floor(i / perRow);
      const x = PAD + col * (size + gap);
      const y = PAD + row * (size + gap);
      const pos = `translate3d(${x}px, ${y}px, 0)`;
      node.style.setProperty('--pos', pos);
      node.style.transform = pos;
      node.style.transition = 'transform 220ms cubic-bezier(.22,1,.36,1)';
    });
    for (const [id, node] of this.#nodes) {
      if (!seen.has(id)) { node.remove(); this.#nodes.delete(id); }
    }
    this.#inner.style.minWidth = `${2 * PAD + perRow * size + (perRow - 1) * gap}px`;
    this.root.classList.toggle('rack--empty', tiles.length === 0);
  }

  /** Consume a tile matching `letter`, preferring a real letter over a blank. */
  take(letter: Letter): Tile | null {
    return this.#tiles.find((t) => !t.isBlank && t.letter === letter)
      ?? this.#tiles.find((t) => t.isBlank)
      ?? null;
  }

  shuffle(): void {
    const order = [...this.#tiles];
    for (let i = order.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [order[i], order[j]] = [order[j] as Tile, order[i] as Tile];
    }
    this.cb.onReorder(order.map((t) => t.id));
    audio.play('select');
  }

  /** Drive the call button's appearance from the rules, not from a guess. */
  setCallState(opts: {
    ready: boolean; rackEmpty: boolean; lockedUntil: number;
    reason: string | null; lastRound: boolean; drawPerRound: number;
    stuck: boolean; stuckCount: number; stuckNeeded: number;
    /** The match is over: the button becomes the way back to the results. */
    finished?: boolean;
  }): void {
    this.#stuck = opts.stuck;
    setAttr(this.#stuckBtn, 'aria-pressed', String(opts.stuck));
    setClass(this.#stuckBtn, 'stuckbtn--on', opts.stuck);
    const waiting = opts.stuckCount > 0;
    setClass(this.#stuckBtn, 'stuckbtn--waiting', waiting && !opts.stuck);
    setText(this.#stuckTally, waiting ? `${opts.stuckCount}/${opts.stuckNeeded}` : '');
    const stuckTitle = opts.stuck
      ? `You said you are stuck — ${opts.stuckCount} of ${opts.stuckNeeded} so far. ` +
        'Tap again to take it back.'
      : waiting
        ? `${opts.stuckCount} of ${opts.stuckNeeded} are stuck. ` +
          'Say so too and everyone draws.'
        : 'Stuck? Say so. If everyone agrees, the whole table draws.';
    setAttr(this.#stuckBtn, 'title', stuckTitle);
    setAttr(this.#stuckBtn, 'aria-label', stuckTitle);
    this.#lockedUntil = opts.lockedUntil;
    setText(this.#emptyMsg,
      `Rack empty — call ${BRAND.callPhraseWords(opts.drawPerRound)}`);
    /*
     * Once the match is over the button has nothing to call. It used to go on
     * offering "WIN IT" over a finished game, and there was no way back to the
     * final table once "Look at the grids" had closed it. Now it is exactly
     * that way back.
     */
    setClass(this.root, 'rack--finished', opts.finished === true);
    if (opts.finished) {
      setDisabled(this.#callBtn, false);
      setClass(this.#callBtn, 'callbtn--ready', true);
      setClass(this.#callBtn, 'callbtn--locked', false);
      setText(this.#callBtn.firstElementChild as HTMLElement, 'RESULTS');
      setAttr(this.#callBtn, 'title', 'The match is over. Show the final table again.');
      setAttr(this.#callBtn, 'aria-label', 'Show the final results');
      setAttr(this.#callBtn, 'aria-disabled', 'false');
      setDisabled(this.#stuckBtn, true);
      return;
    }
    setDisabled(this.#stuckBtn, false);
    setAttr(this.#callBtn, 'aria-label', 'Declare — everyone draws');
    const locked = Date.now() < opts.lockedUntil;
    const secs = Math.ceil((opts.lockedUntil - Date.now()) / 1000);
    const disabled = locked || !opts.rackEmpty;
    setDisabled(this.#callBtn, disabled);
    setClass(this.#callBtn, 'callbtn--ready', opts.ready && !locked);
    setClass(this.#callBtn, 'callbtn--locked', locked);
    // "LOCKED" on its own told a player nothing: not why, and not for how
    // long. The countdown does both — it is visibly temporary, and visibly a
    // consequence rather than a fault.
    setText(this.#callBtn.firstElementChild as HTMLElement,
      locked ? `LOCKED ${secs}s`
        : opts.lastRound && opts.ready ? 'WIN IT'
        : BRAND.callPhrase(opts.drawPerRound));
    setAttr(this.#callBtn, 'title', locked
      ? `Locked for ${secs} more second${secs === 1 ? '' : 's'}. The last call ` +
        'was made on a grid that was not finished, so it cost a short wait. ' +
        'It clears on its own — nothing else is taken away.'
      : opts.reason
        ?? `Call ${BRAND.callPhraseWords(opts.drawPerRound)} — you have used every ` +
           `letter, so everyone at the table draws ${opts.drawPerRound} more and ` +
           'play carries straight on.');
    setAttr(this.#callBtn, 'aria-disabled', String(disabled));
    this.#runCooldown();
  }

  #runCooldown(): void {
    if (this.#coolTimer) { cancelAnimationFrame(this.#coolTimer); this.#coolTimer = 0; }
    const total = 15_000;
    const step = (): void => {
      const left = this.#lockedUntil - Date.now();
      if (left <= 0) { setStyle(this.#cool, 'width', '0%'); this.#coolTimer = 0; return; }
      setStyle(this.#cool, 'width', `${Math.min(100, (left / total) * 100)}%`);
      this.#coolTimer = requestAnimationFrame(step);
    };
    step();
  }

  // -------------------------------------------------------------------

  /**
   * Where in the rack a point is, for a letter being dragged there.
   *
   * On a letter, and well inside it — within the middle (1 − guard), the same
   * rule as on the board — it is a swap. Anywhere else in the rack it slots
   * in beside the nearest letter, on whichever side the point is.
   * Null when the point is not over the rack at all.
   */
  slotAt(clientX: number, clientY: number, movingId: string | null): RackSlot | null {
    const r = this.#strip.getBoundingClientRect();
    const over = clientX >= r.left && clientX <= r.right
      && clientY >= r.top - 8 && clientY <= r.bottom + 8;
    if (!over) return null;
    const guard = clamp(prefs().swapGuard, 0, 1);
    const half = (1 - guard) / 2;
    let nearest = -1;
    let nearestD = Infinity;
    let side = 0;
    this.#tiles.forEach((t, i) => {
      const node = this.#nodes.get(t.id);
      if (!node) return;
      const nr = node.getBoundingClientRect();
      const cx = nr.left + nr.width / 2, cy = nr.top + nr.height / 2;
      const d = Math.hypot(clientX - cx, (clientY - cy) * 2);
      if (d < nearestD) { nearestD = d; nearest = i; side = clientX < cx ? 0 : 1; }
    });
    if (nearest < 0) return { kind: 'insert', index: 0 };
    const t = this.#tiles[nearest]!;
    const nr = this.#nodes.get(t.id)!.getBoundingClientRect();
    const fx = (clientX - (nr.left + nr.width / 2)) / nr.width;
    const inside = clientY >= nr.top - 4 && clientY <= nr.bottom + 4;
    if (t.id !== movingId && guard < 1 && inside && Math.abs(fx) <= half) {
      return { kind: 'swap', tileId: t.id };
    }
    return { kind: 'insert', index: nearest + side };
  }

  /** Show where a dragged letter would go in the rack; null clears it. */
  showSlot(slot: RackSlot | null): void {
    for (const node of this.#nodes.values()) node.classList.remove('tile--swap-target');
    this.#caret.hidden = true;
    if (!slot) return;
    if (slot.kind === 'swap') {
      this.#nodes.get(slot.tileId)?.classList.add('tile--swap-target');
      return;
    }
    const before = this.#tiles[slot.index], after = this.#tiles[slot.index - 1];
    const ref = this.#nodes.get((before ?? after)?.id ?? '');
    if (!ref) return;
    const ir = this.#inner.getBoundingClientRect(), nr = ref.getBoundingClientRect();
    const x = before ? nr.left - ir.left - 3 : nr.right - ir.left + 1;
    Object.assign(this.#caret.style, {
      transform: `translate3d(${x}px, ${nr.top - ir.top}px, 0)`, height: `${nr.height}px`,
    });
    this.#caret.hidden = false;
  }

  /**
   * Where a letter dragged out of the rack is aiming.
   *
   * With the drag reach set below 1, the letter's destination runs ahead of
   * the finger: it is where the finger started plus the distance moved,
   * divided by the reach. At a half, moving the letter half-way up the board
   * aims it at the top. The destination is kept on the board, so once the
   * finger has left the rack there is always somewhere it will land, shown by
   * the green square.
   */
  #aimFor(id: string, start: { x: number; y: number }, clientX: number, clientY: number): DropTarget | null {
    const reach = clamp(prefs().dragReach, 0.2, 1);
    const r = this.board.root.getBoundingClientRect();
    let x = clientX, y = clientY;
    if (reach < 1) {
      // Kept a whole square inside the board's edges, so the square it aims
      // at is always entirely on screen.
      const inset = Math.min(this.board.squareSize * 0.6, r.width / 4, r.height / 4);
      x = clamp(start.x + (clientX - start.x) / reach, r.left + inset, r.right - inset);
      y = clamp(start.y + (clientY - start.y) / reach, r.top + inset, r.bottom - inset);
    } else if (!(x >= r.left && x <= r.right && y >= r.top && y <= r.bottom)) {
      return null;
    }
    return this.board.resolveDrop(this.board.pointToCells(x, y), id);
  }

  /** Put an unfinished drag back as if it had never started. Set by #bindDrag. */
  #cancelDrag: () => void = () => {};
  /** The tile being dragged, if any. */
  #dragging: () => string | null = () => null;

  /**
   * Dragging letters out of the rack.
   *
   * The rack used to listen for the end of a drag on itself only, trusting
   * pointer capture to route the release there. On an iMac with a touchpad a
   * drag was left stranded: a large floating copy of the letter over the
   * board, and the letter itself dimmed in the rack and impossible to pick up.
   * Capture can fail to be taken (Chrome on a Mac can deliver a touchpad
   * tap's press with no live pointer behind it, and asking to capture that
   * throws) or be lost (focus leaving the window, a second press), and then
   * the release lands somewhere else. So now:
   *  - the rest of the gesture is followed on the window, whoever captured it;
   *  - a new press, focus leaving the window, or the letter leaving the rack
   *    puts any unfinished drag back cleanly;
   *  - asking for capture is only a request, and never aborts the gesture.
   */
  #bindDrag(): void {
    let dragId: string | null = null;
    let pointerId: number | null = null;
    let ghost: HTMLElement | null = null;
    let moved = false;
    let start = { x: 0, y: 0 };
    let holdTimer = 0;

    const cancel = (): void => {
      clearTimeout(holdTimer);
      dragId = null;
      pointerId = null;
      moved = false;
      ghost?.remove();
      ghost = null;
      // Belt and braces: nothing dimmed, and no copy anywhere, whatever led here.
      for (const node of this.#nodes.values()) node.classList.remove('tile--ghost');
      for (const stray of document.querySelectorAll('body > .rackdrag')) stray.remove();
      this.showSlot(null);
      this.board.aim(null);
    };
    this.#cancelDrag = cancel;
    this.#dragging = () => dragId;

    on(this.#strip, 'pointerdown', (ev) => {
      const e = ev as PointerEvent;
      const node = (e.target as HTMLElement).closest<HTMLElement>('.tile');
      const id = node?.dataset.tile;
      if (!id) return;
      // A press while another drag is still open: that one is over.
      if (dragId !== null || ghost) cancel();
      dragId = id;
      pointerId = e.pointerId;
      moved = false;
      start = { x: e.clientX, y: e.clientY };
      try { this.#strip.setPointerCapture(e.pointerId); } catch { /* followed on the window instead */ }
      audio.play('pickup');
      // Hold a rack tile to offer a dump. It is the one destructive thing the
      // rack can do, so it asks first rather than firing on a tap.
      // Only where the match allows trading in: otherwise a hold is just a
      // slow start to a drag, and must not buzz or complain.
      clearTimeout(holdTimer);
      if (!this.#tradeEnabled) return;
      holdTimer = window.setTimeout(() => {
        if (moved || dragId !== id) return;
        cancel();
        audio.buzz(20);
        this.cb.onDumpRequest(id);
      }, 500);
    });

    const move = (ev: Event): void => {
      const e = ev as PointerEvent;
      if (!dragId || e.pointerId !== pointerId) return;
      const dx = e.clientX - start.x;
      const dy = e.clientY - start.y;
      if (!moved && Math.hypot(dx, dy) < 8) return;
      if (!moved) {
        clearTimeout(holdTimer);
        const original = this.#nodes.get(dragId);
        if (!original) { cancel(); return; }
        moved = true;
        // A detached clone follows the pointer so the rack can keep scrolling
        // and the original keeps its place until the drop is committed.
        ghost = original.cloneNode(true) as HTMLElement;
        ghost.classList.add('tile--lifted', 'rackdrag');
        ghost.classList.remove('tile--ghost');
        ghost.style.setProperty('--cell', `${this.#tileSize}px`);
        Object.assign(ghost.style, {
          position: 'fixed', left: '0', top: '0', margin: '0',
          pointerEvents: 'none', zIndex: '500', transition: 'none',
        });
        document.body.append(ghost);
        original.classList.add('tile--ghost');
      }
      if (ghost) {
        ghost.style.transform =
          `translate3d(${e.clientX - this.#tileSize / 2}px, ` +
          `${e.clientY - this.#tileSize / 2}px, 0) scale(1.08)`;
      }
      const slot = this.slotAt(e.clientX, e.clientY, dragId);
      this.showSlot(slot);
      this.board.aim(slot ? null : this.#aimFor(dragId, start, e.clientX, e.clientY));
    };

    const end = (ev: Event): void => {
      const e = ev as PointerEvent;
      if (!dragId || e.pointerId !== pointerId) return;
      const id = dragId;
      const wasMoved = moved;
      try { this.#strip.releasePointerCapture(e.pointerId); } catch { /* not held */ }
      cancel();                      // everything visual is put back first

      // Losing a gesture to the browser is neither a tap nor a drop.
      if (e.type === 'pointercancel') return;

      if (!wasMoved) { this.#tapTile(id); return; }

      // Let go in the rack: swap with a letter, or slot in between two.
      const slot = this.slotAt(e.clientX, e.clientY, id);
      if (slot) {
        if (slot.kind === 'swap') this.cb.onSwap(id, slot.tileId);
        else this.cb.onMove(id, { rack: slot.index });
        audio.play('drop');
        return;
      }

      const target = this.#aimFor(id, start, e.clientX, e.clientY);
      if (!target) { audio.play('invalid'); return; }
      if (target.kind === 'swap') this.cb.onSwap(id, target.tileId);
      else this.cb.onPlace(id, target.at);
      audio.play('drop');
      audio.buzz(8);
      /*
       * A blank used to open a dialog the moment it landed, demanding a
       * letter. It no longer does: the engine works out what the blank has to
       * be from the words around it, and asking mid-flow interrupted a player
       * who had not finished building the word the blank was going into.
       * Tapping the blank still opens the dialog for anyone who wants to pin
       * it down on purpose.
       */
    };

    // Followed on the window, so the gesture is seen to its end wherever it
    // goes and whatever holds the capture. Capture phase, so nothing on the
    // page can stop it on the way.
    window.addEventListener('pointermove', move, true);
    window.addEventListener('pointerup', end, true);
    window.addEventListener('pointercancel', end, true);
    // Focus leaving the window mid-drag: the release will never be reported.
    window.addEventListener('blur', () => { if (dragId !== null || ghost) cancel(); });
    document.addEventListener('visibilitychange', () => {
      if (document.visibilityState === 'hidden' && (dragId !== null || ghost)) cancel();
    });
  }

  /**
   * A tap on a rack tile plays it at the board cursor.
   *
   * This is the whole no-keyboard flow: tap a square to aim, then tap letters.
   * Dumping is deliberately *not* on tap — it costs you tiles, and a stray
   * thumb should never be able to trigger it.
   */
  #tapTile(id: string): void {
    const tile = this.#tiles.find((t) => t.id === id);
    if (!tile) return;
    this.board.ensureCursor();
    this.board.placeAtCursor(tile);
    audio.play('drop');
  }
}
